import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { CMS } from '../../utils/CMS';
import { getGeoLocationPosition } from '../../redux/geoLocations/actions';
import { ConnectedLocationsList } from '../LocationAutocomplete/LocationsList';
import { EntryPoint, LegType } from '../../models';
import { DropdownItem } from '../../components/DropdownItem/DropdownItem';
import { TrackingUtils, LocalStorageUtils, withPageContext } from '../../utils';

export class GeoLocationSelector extends PureComponent {
    state = {
        clicked: false,
    };

    hasTrackedOptionShown = false;

    componentDidMount() {
        if (this.props.show) {
            this.trackOptionShown();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (!prevProps.show && this.props.show) {
            this.trackOptionShown();
        }
        const previousLocations = prevProps.geoLocationPosition?.cities || [];
        const locationsWereShown =
            prevState.clicked && previousLocations.length > 0;
        const locationsAreShown =
            this.state.clicked && this.getLocations().length > 0;
        if (!locationsWereShown && locationsAreShown) {
            this.trackLocationsShown();
        }
    }

    trackOptionShown = () => {
        if (this.hasTrackedOptionShown) {
            return;
        }
        this.hasTrackedOptionShown = true;
        this.trackFunnelState('shown');
    };

    getTrackingAction = () => {
        const { activeProduct } = this.props;
        return activeProduct === EntryPoint.FLIGHTS || activeProduct === EntryPoint.TRAINS
            ? TrackingUtils.ACTION[activeProduct.toUpperCase()]
            : '';
    };

    trackFunnelState = (state) => {
        const { trackEvent } = this.props;
        const action = this.getTrackingAction();
        if (!action) {
            return;
        }
        trackEvent({
            action,
            label: TrackingUtils.LABEL.geolocationFunnelV1({ state }),
        });
    };

    trackLocationsShown = () => {
        const { trackEvent } = this.props;
        const action = this.getTrackingAction();
        if (!action) {
            return;
        }
        trackEvent({
            action,
            label: TrackingUtils.LABEL.autocompleteExposureV1({
                field: 'orig',
                source: 'geolocation',
            }),
        });
        trackEvent({
            action,
            label: TrackingUtils.LABEL.autocompleteListV1({
                field: 'orig',
                source: 'geolocation',
                state: 'shown',
                queryLength: 0,
            }),
        });
    };

    onSuccess = (position, transportType) => {
        const { getGeoLocationPosition } = this.props;
        getGeoLocationPosition(position, transportType);
        this.trackFunnelState('accepted');
        TrackingUtils.trackEvent({ action: 'location_request_widget', label: 'location_request_accepted_home' });
    }

    onError = (error, fallbackState = 'position_unavailable') => {
        const errorStates = {
            1: 'denied',
            2: 'position_unavailable',
            3: 'timeout',
        };
        LocalStorageUtils.setJSON('geoLocAllowed', false);
        this.trackFunnelState(errorStates[error?.code] || fallbackState);
        TrackingUtils.trackEvent({ action: 'location_request_widget', label: 'location_request_denied_home' });
    }

    onClickGeoButton = (activeProduct) => {
        const transportType = activeProduct === 'trains' ? 'TRAIN' : 'FLIGHT';
        this.trackFunnelState('clicked');
        TrackingUtils.trackEvent({ action: 'location_request_button', label: 'location_request_clicked_origin' });
        if (navigator.geolocation) {
            this.setState({ clicked: true });
            navigator.geolocation.getCurrentPosition(
                (position) => this.onSuccess(position, transportType),
                this.onError
            );
        } else {
            this.onError(null, 'unsupported');
        }
    }

    onClickLocation = (location, { position } = {}) => {
        const { onClickItem, trackEvent } = this.props;
        const action = this.getTrackingAction();
        if (action) {
            trackEvent({
                action,
                label: TrackingUtils.LABEL.autocompleteSelectV1({
                    field: 'orig',
                    source: 'geolocation',
                    position,
                    queryLength: 0,
                }),
            });
        }
        onClickItem(location);
    };

    getLocations = () => {
        const { geoLocationPosition } = this.props;
        return geoLocationPosition && geoLocationPosition.cities ? geoLocationPosition.cities : [];
    }

    render() {
        const { show, activeProduct } = this.props;
        const { clicked } = this.state;
        const locations = this.getLocations();
        return show && (
            <React.Fragment>
                {!clicked && (
                    <DropdownItem
                        onClick={() => this.onClickGeoButton(activeProduct)}
                        iconClass='odf-icon-location'
                        isFixedWidth
                        isShowIcon
                    >
                        <span><CMS id='airportSelector.geolocation.current' /></span>
                    </DropdownItem>
                )}
                { clicked && locations && (
                    <ConnectedLocationsList
                        displayedWord=''
                        suggestionsList={locations}
                        onClickItem={this.onClickLocation}
                        departureOrArrival={LegType.DEPARTURE}
                        isFixedWidth
                    />
                )}
            </React.Fragment>
        );
    }
}

GeoLocationSelector.propTypes = {
    getGeoLocationPosition: PropTypes.func.isRequired,
    onClickItem: PropTypes.func.isRequired,
    geoLocationPosition: PropTypes.object.isRequired,
    show: PropTypes.bool,
    activeProduct: PropTypes.string,
    trackEvent: PropTypes.func,
};

GeoLocationSelector.defaultProps = {
    show: false,
    trackEvent: () => {},
};

const mapStateToProps = ({ geoLocations }) => ({
    geoLocationPosition: geoLocations.geoLocationPosition,
});

const connected = connect(mapStateToProps, {
    getGeoLocationPosition,
})(withPageContext(GeoLocationSelector));

export { connected as ConnectedGeoLocationSelector };
